import { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { ArrowRight, FileText } from 'lucide-react-native';

import { Badge, Button, Sheet, TextField } from '@/components/ui';
import { usarFormulario } from '@/services/firebase/formularios';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import type { Formulario } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  formulario: Formulario | null;
}

export function UsarFormularioSheet({ visible, onClose, formulario }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const [cantidad, setCantidad] = useState('1');
  const [numeroTalonario, setNumeroTalonario] = useState('');
  const [solicitadoPor, setSolicitadoPor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !formulario) return;
    setCantidad('1');
    setNumeroTalonario('');
    setSolicitadoPor('');
    setMotivo('');
    setError(null);
    setSaving(false);
  }, [visible, formulario]);

  if (!formulario) return null;

  const cantidadN = Number(cantidad);
  const stockTras = (formulario.stock ?? 0) - (Number.isFinite(cantidadN) ? cantidadN : 0);
  const invalida = !Number.isFinite(cantidadN) || cantidadN <= 0 || cantidadN > (formulario.stock ?? 0);

  const onSubmit = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    if (!Number.isFinite(cantidadN) || cantidadN <= 0) return setError('Cantidad inválida.');
    if (cantidadN > (formulario.stock ?? 0)) return setError(`Stock insuficiente. Quedan ${formulario.stock ?? 0}.`);

    setSaving(true);
    try {
      await usarFormulario({
        formularioId: formulario.id,
        cantidad: cantidadN,
        numeroTalonario: numeroTalonario.trim() || undefined,
        solicitadoPor: solicitadoPor.trim() || undefined,
        motivo: motivo.trim() || undefined,
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo registrar el uso.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title="Registrar uso de formulario"
      subtitle={formulario.nombre}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label="Registrar uso"
            iconLeft={<ArrowRight size={16} color={colors.white} />}
            onPress={onSubmit}
            loading={saving}
            disabled={invalida}
            variant="success"
          />
        </>
      }
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          padding: 14,
          borderRadius: radius['2xl'],
          backgroundColor: colors.crema[50],
        }}
      >
        <View
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            backgroundColor: colors.magenta[100],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FileText size={20} color={colors.magenta[700]} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {formulario.nombre}
          </Text>
          {formulario.descripcion ? (
            <Text style={{ fontSize: 12, color: colors.ink[500] }}>{formulario.descripcion}</Text>
          ) : null}
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8 }}>
            STOCK
          </Text>
          <Text
            style={{
              fontFamily: typography.display.fontFamily,
              fontSize: 20,
              fontWeight: typography.weights.bold,
              color: formulario.stockBajo ? colors.warning[600] : colors.success[600],
            }}
          >
            {formulario.stock}
          </Text>
        </View>
      </View>

      <View style={{ flex: 1, minWidth: 140 }}>
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700], marginBottom: 6 }}>
          Cantidad
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Pressable
            onPress={() => setCantidad(String(Math.max(1, cantidadN - 1)))}
            style={({ hovered }) => ({
              width: 44,
              height: 50,
              borderRadius: radius.xl,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
              borderWidth: 1.5,
              borderColor: colors.ink[200],
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Text style={{ fontSize: 22, color: colors.ink[700], fontWeight: typography.weights.bold }}>−</Text>
          </Pressable>
          <TextField containerStyle={{ flex: 1 }} value={cantidad} onChangeText={setCantidad} keyboardType="numeric" />
          <Pressable
            onPress={() => setCantidad(String(Math.min((formulario.stock ?? 0), cantidadN + 1)))}
            style={({ hovered }) => ({
              width: 44,
              height: 50,
              borderRadius: radius.xl,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hovered ? colors.magenta[200] : colors.magenta[100],
              borderWidth: 1.5,
              borderColor: colors.magenta[200],
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Text style={{ fontSize: 22, color: colors.magenta[700], fontWeight: typography.weights.bold }}>+</Text>
          </Pressable>
        </View>
      </View>

      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <TextField
          containerStyle={{ flex: 1, minWidth: 200 }}
          label="N° de talonario"
          placeholder="Ej: 001-050"
          value={numeroTalonario}
          onChangeText={setNumeroTalonario}
        />
        <TextField
          containerStyle={{ flex: 1, minWidth: 200 }}
          label="Quién solicitó"
          placeholder="Ej: Dra. Jazmín / Consultorio 1"
          value={solicitadoPor}
          onChangeText={setSolicitadoPor}
        />
      </View>

      <TextField
        label="Motivo / detalle (opcional)"
        placeholder="Castración / vacuna antirrábica / etc."
        value={motivo}
        onChangeText={setMotivo}
        multiline
      />

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 14,
          borderRadius: radius.xl,
          backgroundColor: colors.success[50],
        }}
      >
        <View>
          <Text
            style={{
              fontSize: 11,
              fontWeight: typography.weights.bold,
              color: colors.success[600],
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            Resumen
          </Text>
          <Text
            style={{
              fontFamily: typography.display.fontFamily,
              fontSize: 16,
              fontWeight: typography.weights.bold,
              color: colors.ink[900],
            }}
          >
            {Number.isFinite(cantidadN) ? cantidadN : 0} × {formulario.nombre}
          </Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 1, textTransform: 'uppercase' }}>
            Stock tras uso
          </Text>
          <Text
            style={{
              fontFamily: typography.display.fontFamily,
              fontSize: 20,
              fontWeight: typography.weights.bold,
              color:
                stockTras <= 0
                  ? colors.danger[600]
                  : stockTras <= (formulario.stockMin ?? 0)
                    ? colors.warning[600]
                    : colors.ink[800],
            }}
          >
            {Number.isFinite(stockTras) ? stockTras : '—'}
          </Text>
          {stockTras <= 0 && Number.isFinite(stockTras) ? (
            <Badge label="Quedará agotado" tone="danger" />
          ) : stockTras <= (formulario.stockMin ?? 0) ? (
            <Badge label="Quedará en bajo" tone="warning" />
          ) : null}
        </View>
      </View>

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>
            {error}
          </Text>
        </View>
      ) : null}
    </Sheet>
  );
}
